import { resolveAppLanguage } from '../../../i18n/language';

/**
 * Coerce a location picker value / FK into an integer primary key for write payloads.
 * Never returns a nested location object — POST/PATCH expect an integer only.
 * @param {unknown} value - nested `{ id }`, numeric id, or numeric string
 * @returns {number|null}
 */
export function toLocationWritePk(value) {
  if (value == null || value === '') return null;
  const raw =
    typeof value === 'object' && value !== null
      ? value.id ?? value.pk
      : value;
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Normalize GET shipment/unit location for edit pickers (nested object preferred).
 * @param {{ location?: unknown, location_id?: unknown }} record
 * @returns {{ id: number, [key: string]: unknown }|null}
 */
export function locationOptionFromApi(record) {
  if (!record) return null;
  const loc = record.location;
  if (loc && typeof loc === 'object' && !Array.isArray(loc)) {
    const id = toLocationWritePk(loc);
    if (id == null) return null;
    return { ...loc, id };
  }
  const id = toLocationWritePk(
    record.location_id != null ? record.location_id : typeof loc === 'number' || typeof loc === 'string' ? loc : null
  );
  if (id == null) return null;
  return { id };
}

/**
 * Display label for a Location API row ({ name, name_ar, id }).
 * Arabic UI → name_ar (fallback name); English and Chinese → name.
 */
export function getLocationDisplayName(row, i18nLanguage) {
  if (!row) return '';
  const name = row.name != null ? String(row.name).trim() : '';
  const nameAr = row.name_ar != null ? String(row.name_ar).trim() : '';
  const lang = resolveAppLanguage(i18nLanguage);
  if (lang === 'ar') return nameAr || name;
  return name;
}

/**
 * List/detail rows: nested `location` object, or flat `location_name` / `location_name_ar`,
 * or `location` as a plain string (some shipment/unit serializers).
 */
export function getLocationDisplayNameFromRow(row, i18nLanguage) {
  if (!row) return '';
  if (row.location != null && typeof row.location === 'object') {
    const loc = row.location;
    const nameNested = loc.name != null ? String(loc.name).trim() : '';
    const nameArNested = loc.name_ar != null ? String(loc.name_ar).trim() : '';
    const nameFlat = row.location_name != null ? String(row.location_name).trim() : '';
    const nameArFlat =
      row.location_name_ar != null
        ? String(row.location_name_ar).trim()
        : row.location_ar != null
          ? String(row.location_ar).trim()
          : '';
    return getLocationDisplayName(
      {
        name: nameNested || nameFlat,
        name_ar: nameArNested || nameArFlat,
      },
      i18nLanguage
    );
  }
  if (typeof row.location === 'string' && row.location.trim()) {
    const nameAr =
      row.location_ar != null
        ? String(row.location_ar).trim()
        : row.location_name_ar != null
          ? String(row.location_name_ar).trim()
          : '';
    return getLocationDisplayName({ name: row.location.trim(), name_ar: nameAr }, i18nLanguage);
  }
  return getLocationDisplayName(
    {
      name: row.location_name != null ? String(row.location_name) : '',
      name_ar: row.location_name_ar != null ? String(row.location_name_ar) : '',
    },
    i18nLanguage
  );
}
